import { Box, Stack } from "@mui/material";
import { useCodeListQuery } from "Api/Queries/CodeList/useCodeListQuery";
import { Dropdown } from "Components/Shared/Inputs/Form/Dropdown";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import { CzechiaFlag } from "Components/Shared/LanguageSwitcher/Flags/Czechia";
import { GermanyFlag } from "Components/Shared/LanguageSwitcher/Flags/Germany";
import { GreatBritainFlag } from "Components/Shared/LanguageSwitcher/Flags/GreatBritain";
import { PolandFlag } from "Components/Shared/LanguageSwitcher/Flags/Poland";
import { SlovakiaFlag } from "Components/Shared/LanguageSwitcher/Flags/Slovakia";
import { UkraineFlag } from "Components/Shared/LanguageSwitcher/Flags/Ukraine";
import { VietnamFlag } from "Components/Shared/LanguageSwitcher/Flags/Vietnam";
import { CountryCode } from "Infrastructure/Api/Api";
import {
  DEFAULT_PHONE_PREFIX,
  PHONE_FALLBACK_MAX_DIGITS,
  PHONE_FALLBACK_MIN_DIGITS,
} from "Utils/PhoneUtils";
import { type ReactNode, useMemo } from "react";
import {
  type Control,
  type FieldPath,
  type FieldValues,
  useWatch,
} from "react-hook-form";

// Per-country digit limits come from the code list (phoneFormats[].minDigits/maxDigits,
// sourced from CountryCodeExtensions on the backend — the same values IsValid() enforces),
// so the mask always matches what the API accepts. The input unmasks, so the form value
// stays digits-only.

/** "000 000 000" for 9/9, "000 000 000[ 0]" for 9/10 — digits grouped by threes,
 *  positions above the minimum optional. */
const buildNationalNumberMask = (
  minDigits: number,
  maxDigits: number,
): string => {
  let mask = "";
  for (let position = 1; position <= maxDigits; position++) {
    const separator = position > 1 && (position - 1) % 3 === 0 ? " " : "";
    mask += position <= minDigits ? `${separator}0` : `[${separator}0]`;
  }
  return mask;
};

const FALLBACK_MASK = buildNationalNumberMask(
  PHONE_FALLBACK_MIN_DIGITS,
  PHONE_FALLBACK_MAX_DIGITS,
);

// The shared flag SVGs are square and cropped to a circle for the language switcher;
// overriding the size/shape props renders them as the flat rectangles conventional
// for dial-code pickers (the simple geometric designs stretch to 3:2 without artifacts).
const FLAG_PROPS = {
  width: 21,
  height: 14,
  preserveAspectRatio: "none",
  style: { borderRadius: 2, flexShrink: 0 },
} as const;

/** Flags shown next to the dial codes; a prefix without an entry renders without a flag. */
const PREFIX_FLAGS: Partial<Record<CountryCode, ReactNode>> = {
  [CountryCode.CZ]: <CzechiaFlag {...FLAG_PROPS} />,
  [CountryCode.SK]: <SlovakiaFlag {...FLAG_PROPS} />,
  [CountryCode.PL]: <PolandFlag {...FLAG_PROPS} />,
  [CountryCode.DE]: <GermanyFlag {...FLAG_PROPS} />,
  [CountryCode.VN]: <VietnamFlag {...FLAG_PROPS} />,
  [CountryCode.UA]: <UkraineFlag {...FLAG_PROPS} />,
  [CountryCode.GB]: <GreatBritainFlag {...FLAG_PROPS} />,
};

/** Offered while the code list is loading — the default market only. */
const DEFAULT_PREFIXES = [
  {
    code: DEFAULT_PHONE_PREFIX,
    name: DEFAULT_PHONE_PREFIX,
    icon: PREFIX_FLAGS[CountryCode.CZ],
  },
];

type Props<FormType extends FieldValues> = {
  /** Path to the { prefix, number } phone object inside the form model. */
  name: FieldPath<FormType>;
  control: Control<FormType>;
  label?: string;
  disabled?: boolean;
  /** Focuses the national-number input on mount (e.g. inside an edit dialog). */
  autoFocus?: boolean;
};

/**
 * Phone field bound to a { prefix, number } form value: dial-code dropdown fed by the
 * code list (falls back to +420 while the code list loads) next to the national number input.
 */
export const PhoneInput = <T extends FieldValues>({
  name,
  control,
  label,
  disabled,
  autoFocus,
}: Props<T>) => {
  const { data: codeList } = useCodeListQuery();
  const prefix = useWatch({
    control,
    name: `${name}.prefix` as FieldPath<T>,
  });

  const prefixes = useMemo(
    () =>
      codeList?.phoneFormats?.length
        ? codeList.phoneFormats.map(({ prefix: dialCode, countryCode }) => ({
            code: dialCode,
            name: dialCode,
            icon: PREFIX_FLAGS[countryCode],
          }))
        : DEFAULT_PREFIXES,
    [codeList],
  );

  const selectedPrefix = codeList?.phoneFormats?.find(
    (item) => item.prefix === prefix,
  );
  const mask = selectedPrefix
    ? buildNationalNumberMask(
        selectedPrefix.minDigits,
        selectedPrefix.maxDigits,
      )
    : FALLBACK_MASK;

  return (
    // "end" keeps the two input boxes aligned — the label sits only above the dropdown,
    // so top alignment would push the number input up against the label row.
    <Stack direction="row" gap={1} alignItems="end">
      <Box minWidth={110}>
        <Dropdown
          control={control}
          name={`${name}.prefix` as FieldPath<T>}
          codeList={prefixes}
          label={label}
          disabled={disabled}
        />
      </Box>
      <Box flex={1}>
        <FormInput
          control={control}
          name={`${name}.number` as FieldPath<T>}
          type="tel"
          mask={mask}
          disabled={disabled}
          autoFocus={autoFocus}
        />
      </Box>
    </Stack>
  );
};
